import { ContextualInfoButton } from "@/components/buttons/ContextualInfoButton";
import { AddressLookup } from "@/components/inputs/AddressLookup";
import { RiskBandsAdvancedSettings, RiskBandsAdvancedSettingsRefs } from "@/components/layouts/RiskBandsAdvancedSettings";
import { getArchetypes, getRiskBandsMode } from "@/features/branding";
import { isPointWithinSystemBounds } from "@/features/geographic_data";
import { HubMapLocationPicker } from "@/features/maps";
import { defaultBuildYear } from "@/features/risk_bands";
import { invalidLatLng, LatLng, latLngSchema } from "@/lib/crg_apis/types/latlng";
import { store } from "@/store";
import { supabaseApi, useGetToolUsageQuery } from "@/store/services/supabase";
import ScreeningFairUseWarning from "@/tools/risk-bands/components/ScreeningFairUseWarning";
import { ScreeningLimitReview, ScreeningReviewModalRefs } from "@/tools/risk-bands/components/ScreeningLimitReview";
import { tourSingleResponseAbc, tourSingleResponseHalo } from "@/tools/risk-bands/tutorials/data/single";
import { SingleAssetScreenTutorial } from "@/tools/risk-bands/tutorials/single";
import { RiskBandsAdvancedSettingsValues, RiskBandsArchetype } from "@/tools/risk-bands/types";
import { Tool, ToolProps } from "@/types/tools";
import { limitLatLngTo6dp } from "@/utils/geocoding";
import { formatSettingSwitchValue } from "@/utils/string";
import { CompressOutlined } from "@ant-design/icons";
import { Alert, Button, Col, Divider, Input, Row, Space, Typography } from "antd";
import { LatLngLiteral } from "leaflet";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { PointModalTabs, SingleResultModal, SingleResultModalRefs } from "./components/SingleResultModal";
const {
  TextArea
} = Input;
const {
  Title
} = Typography;

/**
 * Retrieve the risk bands for a single asset.
 */
const RiskBandsSingleAssetTool = (props: ToolProps) => {
  const {
    t
  } = useTranslation();

  // Dummy data used for displaying the tour.
  const tourResponse = {
    abc: tourSingleResponseAbc,
    halo: tourSingleResponseHalo,
    simple: tourSingleResponseHalo
  }[getRiskBandsMode()];
  const addressRef = useRef(null);
  const reviewRef = useRef(null);
  const mapRef = useRef(null);
  const [settingsRefs, setSettingsRefs] = useState<RiskBandsAdvancedSettingsRefs | undefined>(undefined);
  const [modalRefs, setModalRefs] = useState<SingleResultModalRefs | undefined>(undefined);
  const [reviewModalRefs, setReviewModalRefs] = useState<ScreeningReviewModalRefs | undefined>(undefined);
  const archetypes = getArchetypes();
  const [address, setAddress] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [tutorialSelectedTab, setTutorialSelectedTab] = useState<PointModalTabs | undefined>(undefined);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [position, setPosition] = useState(invalidLatLng);
  const [isWithinBounds, setIsWithinBounds] = useState(true);
  const defaultArchetype = archetypes[0];
  const [advancedSettings, setAdvancedSettings] = useState<RiskBandsAdvancedSettingsValues>({
    archetype: defaultArchetype.value as RiskBandsArchetype,
    buildYear: {
      source: "archetype",
      amount: defaultArchetype.defaultBuildYear || defaultBuildYear
    },
    heightAboveGround: {
      source: "archetype",
      amount: defaultArchetype.defaultHeightAboveGround || 0
    },
    anomalyManagementRp: true,
    defences: true
  });
  const {
    data: toolUsage
  } = useGetToolUsageQuery({
    activeQuotaOnly: true
  });
  if (!toolUsage) {
    return <></>;
  }
  const currentUsage = toolUsage.singleQuotaMode && toolUsage.singleQuotaUsage ? toolUsage.singleQuotaUsage : toolUsage.screening;
  const handleChange = (newAddress: string, newPosition: LatLng) => {
    setAddress(newAddress);
    setPosition(newPosition);
    setIsWithinBounds(isPointWithinSystemBounds(newPosition));
  };
  const handleReset = () => {
    setIsModalOpen(false);
    setAddress("");
    setPosition(invalidLatLng);
    setIsWithinBounds(true);
    setAdvancedSettings({
      archetype: defaultArchetype.value as RiskBandsArchetype,
      buildYear: {
        source: "archetype",
        amount: defaultArchetype.defaultBuildYear || defaultBuildYear
      },
      heightAboveGround: {
        source: "archetype",
        amount: defaultArchetype.defaultHeightAboveGround || 0
      },
      anomalyManagementRp: true,
      defences: true
    });
  };
  const showPointOutsideBoundsWarning = !isWithinBounds && position !== invalidLatLng;
  return <>
      {currentUsage && <ScreeningFairUseWarning toolUsage={toolUsage} currentUsage={currentUsage} />}
      <Row gutter={[24, 0]} className="mb-8 min-h-[400px] w-full" data-sentry-element="Row" data-sentry-source-file="single.tsx">
        <Col flex={1} ref={mapRef} data-sentry-element="Col" data-sentry-source-file="single.tsx">
          <Title level={5} className="mt-0 pt-0" data-sentry-element="Title" data-sentry-source-file="single.tsx">
            Select A Point
          </Title>
          <HubMapLocationPicker className="h-[70vh] overflow-hidden rounded-md border" onChange={(position: LatLngLiteral) => {
          const position6dp = limitLatLngTo6dp(position);
          handleChange(`(${position6dp.lat}, ${position6dp.lng})`, position);
        }} position={latLngSchema.safeParse(position).success ? position : undefined} data-sentry-element="HubMapLocationPicker" data-sentry-source-file="single.tsx" />
        </Col>
        <Col flex={0} className="w-full max-w-[320px]" data-sentry-element="Col" data-sentry-source-file="single.tsx">
          <Row gutter={[0, 12]} data-sentry-element="Row" data-sentry-source-file="single.tsx">
            <Col span={24} ref={addressRef} data-sentry-element="Col" data-sentry-source-file="single.tsx">
              <Title level={5} className="mt-0 pt-0" data-sentry-element="Title" data-sentry-source-file="single.tsx">
                <Space data-sentry-element="Space" data-sentry-source-file="single.tsx">
                  Search For A Location <ContextualInfoButton id="enteringLocationsAndAddresses" data-sentry-element="ContextualInfoButton" data-sentry-source-file="single.tsx" />
                </Space>
              </Title>
              <AddressLookup address={address} onChange={handleChange} onClear={() => handleChange("", invalidLatLng)} placeholder="" data-sentry-element="AddressLookup" data-sentry-source-file="single.tsx">
                <TextArea rows={2} placeholder="Type in an address, or click the map to adjust the pin" className="p-2 text-sm placeholder:text-sm placeholder:text-gray-400" data-sentry-element="TextArea" data-sentry-source-file="single.tsx" />
              </AddressLookup>
            </Col>

            {showPointOutsideBoundsWarning && <Col span={24}>
                <Alert showIcon type="error" message="Selected point is outside of system bounds and therefore cannot be processed." />
              </Col>}
            <Divider className="m-0" data-sentry-element="Divider" data-sentry-source-file="single.tsx" />
            <Col span={24} className="flex items-center justify-between p-0" data-sentry-element="Col" data-sentry-source-file="single.tsx">
              <Title level={5} className="!m-0 p-0" data-sentry-element="Title" data-sentry-source-file="single.tsx">
                Settings
              </Title>
              <Button onClick={handleReset} type="text" className="opacity-70" size="small" data-sentry-element="Button" data-sentry-source-file="single.tsx">
                Reset
              </Button>
            </Col>
            <Col span={24} data-sentry-element="Col" data-sentry-source-file="single.tsx">
              <RiskBandsAdvancedSettings isAdvancedOpen={isAdvancedOpen} values={advancedSettings} setRefs={setSettingsRefs} setValues={setAdvancedSettings} data-sentry-element="RiskBandsAdvancedSettings" data-sentry-source-file="single.tsx" />
            </Col>
            <Divider className="m-0" data-sentry-element="Divider" data-sentry-source-file="single.tsx" />
            {<Col span={24}>
                <>
                  <ScreeningFairUseWarning toolUsage={toolUsage} currentUsage={currentUsage} hideDetail />
                  <ScreeningLimitReview isOpen={isReviewModalOpen} setRefs={setReviewModalRefs} title="Single Asset Screen" uploadedAssetCount={1} continueText={t("actions:check")} onBack={() => {
                setIsReviewModalOpen(false);
              }} onContinue={() => {
                setIsReviewModalOpen(false);
                setIsModalOpen(true);
              }} basicSettings={[{
                label: t("terms:location"),
                value: address
              }, {
                label: t("terms:archetype"),
                value: advancedSettings.archetype
              }, {
                label: t("terms:buildYear"),
                value: advancedSettings.buildYear.amount
              }, {
                label: t("terms:heightAboveGround"),
                value: advancedSettings.heightAboveGround.amount
              }, {
                label: "Total assets",
                value: 1
              }]} advancedSettings={[{
                label: t("tools:settings.anomalyManagementRp.title"),
                value: formatSettingSwitchValue(advancedSettings.anomalyManagementRp)
              }, {
                label: t("tools:settings.defences.title"),
                value: formatSettingSwitchValue(advancedSettings.defences)
              }]} />
                </>
              </Col>}

            <Col span={24} data-sentry-element="Col" data-sentry-source-file="single.tsx">
              <Button ref={reviewRef} block type="primary" disabled={!address || !isWithinBounds} onClick={() => setIsReviewModalOpen(true)} data-sentry-element="Button" data-sentry-source-file="single.tsx">
                Review
              </Button>
            </Col>
            {isModalOpen && <SingleResultModal address={address} advancedSettings={advancedSettings} customResponse={props.isTutorialOpen ? tourResponse : undefined} onClose={() => {
            setIsModalOpen(false);
            store.dispatch(supabaseApi.endpoints.getToolUsage.initiate({
              activeQuotaOnly: true
            }, {
              forceRefetch: true
            }));
          }} onReset={handleReset} position={position} setRefs={setModalRefs} tutorialSelectedTab={tutorialSelectedTab} />}
          </Row>
        </Col>
      </Row>

      <SingleAssetScreenTutorial open={props.isTutorialOpen} onClose={() => {
      props.setIsTutorialOpen(false);
      setIsModalOpen(false);
      setAddress("");
      setPosition(invalidLatLng);
    }} onChange={step => {
      setIsAdvancedOpen(step >= 6);
      setIsReviewModalOpen(step === 8);

      // Open modal if in second half of tutorial.
      setIsModalOpen(step > 8);
      if (step === 10 || step == 11) {
        setTutorialSelectedTab("risk");
      } else if (step === 12) {
        setTutorialSelectedTab("significantHazards");
      } else if (step === 13) {
        setTutorialSelectedTab("exposure");
      } else {
        setTutorialSelectedTab(undefined);
      }
    }} setState={data => {
      setAddress(data.address);
      setPosition(data.position);
    }} refs={{
      address: addressRef,
      archetype: settingsRefs?.archetype ?? {
        current: null
      },
      buildYear: settingsRefs?.buildYear ?? {
        current: null
      },
      heightAboveGound: settingsRefs?.heightAboveGround ?? {
        current: null
      },
      advancedCollapse: settingsRefs?.advancedCollapse ?? {
        current: null
      },
      advancedSettings: settingsRefs,
      reviewModal: reviewModalRefs,
      reviewModalTrigger: reviewRef,
      map: mapRef,
      modal: modalRefs
    }} data-sentry-element="SingleAssetScreenTutorial" data-sentry-source-file="single.tsx" />
    </>;
};
export default {
  id: "single",
  category: "screening",
  keyPrefix: "screening.single",
  icon: <CompressOutlined />,
  render: props => <RiskBandsSingleAssetTool {...props} />,
  maxWidth: "1440px",
  hasTutorial: true,
  showUsage: false
} as Tool;